import * as Highcharts from 'highcharts';

/**
 * Shared Highcharts theming for the data-view volume charts (Harvesting Log,
 * HTTP Request Log). Highcharts paints colors as SVG presentation attributes,
 * where `var()` cannot work - so fills are baked to literals via cssToken()
 * and the chart options must re-derive when the theme flips (the components
 * read their darkMode signal inside the computed for exactly that).
 */
export const cssToken = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/**
 * Chart chrome shared by the data-view charts: transparent background on the
 * widget card, no title/credits/built-in legend (a custom HTML .chart-legend
 * sits below the chart), stacked animation-free columns.
 */
export function dataViewChartBase(): Highcharts.Options {
  return {
    chart: {
      height: 240,
      backgroundColor: 'transparent',
      spacing: [4, 0, 0, 0],
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    plotOptions: {
      column: {
        stacking: 'normal',
        borderWidth: 0,
        cursor: 'pointer',
        animation: false,
      },
      line: { animation: false },
    },
  };
}

/**
 * Axis label style. Applied by Highcharts as element CSS (not an SVG
 * attribute), so `var()` is fine here and tracks the theme live.
 */
export function dataViewAxisLabels(): Highcharts.XAxisLabelsOptions {
  return {
    style: { fontSize: '10px', color: 'var(--color-text-secondary)' },
  };
}

/**
 * Themed tooltip chrome (Highcharts' default is white in both themes):
 * raised surface, overlay border, primary text. Merge into the view's own
 * tooltip options (`{ ...dataViewTooltipTheme(token), shared: true, ... }`).
 */
export function dataViewTooltipTheme(
  token: (name: string) => string = cssToken,
): Highcharts.TooltipOptions {
  return {
    backgroundColor: token('--color-surface-raised'),
    borderColor: token('--color-border-overlay'),
    style: { color: token('--color-text-primary') },
  };
}
